// Every screen, who may open it, and the module it belongs to. Screens load on first visit (their own chunk).
import { Suspense, lazy, type ComponentType, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { useSession } from "../../lib/session";
import type { Role } from "../../lib/types";
import { PageSkeleton, type SkeletonKind } from "../../ui/loading";
import { ROUTE_MOD } from "./nav";

/** A chunk from before a deploy is gone after it: reload once to pick up the new build. */
function page<M>(load: () => Promise<M>, name: keyof M): ComponentType {
  return lazy(() => load().then(
    (m) => { sessionStorage.removeItem("chunk-reload"); return { default: m[name] as ComponentType }; },
    (e) => { if (sessionStorage.getItem("chunk-reload")) throw e; sessionStorage.setItem("chunk-reload", "1"); location.reload(); return new Promise<never>(() => {}); },
  ));
}

const Today = page(() => import("../today/TodayPage"), "TodayPage");
const Accounts = page(() => import("../accounts/AccountsPage"), "AccountsPage");
const Account = page(() => import("../accounts/AccountPage"), "AccountPage");
const Tasks = page(() => import("../tasks/TasksPage"), "TasksPage");
const Leads = page(() => import("../leads/LeadsPage"), "LeadsPage");
const Lead = page(() => import("../leads/LeadPage"), "LeadPage");
const Quotes = page(() => import("../quotes/QuotesPage"), "QuotesPage");
const QuoteNew = page(() => import("../quotes/QuoteNewPage"), "QuoteNewPage");
const Quote = page(() => import("../quotes/QuotePage"), "QuotePage");
const Approvals = page(() => import("../quotes/ApprovalsPage"), "ApprovalsPage");
const MyNumbers = page(() => import("../dash/MePage"), "MePage");
const Team = page(() => import("../dash/TeamPage"), "TeamPage");
const Money = page(() => import("../money/MoneyPage"), "MoneyPage");
const Ops = page(() => import("../ops/OpsPage"), "OpsPage");
const Matches = page(() => import("../ops/MatchesPage"), "MatchesPage");
const Admin = page(() => import("../admin/AdminPage"), "AdminPage");
const Visits = page(() => import("../visits/VisitsPage"), "VisitsPage");
const Pipeline = page(() => import("../pipeline/PipelinePage"), "PipelinePage");
const Inbox = page(() => import("../inbox/InboxPage"), "InboxPage");
const Dial = page(() => import("../campaigns/DialPage"), "DialPage");
const Campaigns = page(() => import("../campaigns/CampaignsPage"), "CampaignsPage");
const Campaign = page(() => import("../campaigns/CampaignPage"), "CampaignPage");
const Floor = page(() => import("../floor/FloorPage"), "FloorPage");
const Qa = page(() => import("../qa/QaPage"), "QaPage");
const Reports = page(() => import("../reports/ReportsPage"), "ReportsPage");
const Automations = page(() => import("../automations/AutomationsPage"), "AutomationsPage");
const Calls = page(() => import("../calls/CallsPage"), "CallsPage");

const FIELD: Role[] = ["rep", "caller", "manager", "admin"];
const BOOK: Role[] = ["rep", "manager", "admin"];
const LEADS: Role[] = ["manager", "admin"];
// path, who may open it, the screen, and the wireframe shown while it first loads
const ROUTES: [string, Role[], ComponentType, SkeletonKind][] = [
  ["/today", FIELD, Today, "dash"], ["/accounts", BOOK, Accounts, "list"], ["/account/:id", BOOK, Account, "detail"], ["/tasks", FIELD, Tasks, "list"],
  ["/leads", FIELD, Leads, "board"], ["/lead/:id", FIELD, Lead, "detail"], ["/quotes", BOOK, Quotes, "list"], ["/quote/new", BOOK, QuoteNew, "detail"],
  ["/quote/:id", BOOK, Quote, "detail"], ["/approvals", LEADS, Approvals, "cards"], ["/me", ["rep", "manager"], MyNumbers, "dash"], ["/team", LEADS, Team, "dash"],
  ["/money", LEADS, Money, "list"], ["/ops", ["ops", "admin"], Ops, "cards"], ["/matches", ["rep", "manager", "admin", "ops"], Matches, "cards"],
  ["/admin", ["admin"], Admin, "list"], ["/visits", ["rep", "manager"], Visits, "list"], ["/pipeline", BOOK, Pipeline, "board"], ["/inbox", FIELD, Inbox, "list"],
  ["/dial", ["caller", "rep", "manager"], Dial, "cards"], ["/campaigns", LEADS, Campaigns, "list"], ["/campaign/:id", LEADS, Campaign, "list"],
  ["/floor", LEADS, Floor, "list"], ["/qa", FIELD, Qa, "cards"], ["/reports", LEADS, Reports, "dash"], ["/automations", ["admin"], Automations, "cards"],
  ["/calls", FIELD, Calls, "list"],
];

function Guard({ roles, seg, children }: { roles: Role[]; seg: string; children: ReactNode }) {
  const { me, modOn, home } = useSession();
  if (!roles.includes(me.role) || !modOn(ROUTE_MOD[seg])) return <Navigate to={home} replace />;
  return children;
}

export function AppRoutes() {
  const { home } = useSession();
  const { pathname } = useLocation();
  // a new screen (or another account) shows its wireframe at once; a filter or tab change on the same screen keeps it
  // up, dimmed, until the new data is in (the navigation is a transition)
  return (
    <Routes>
      {ROUTES.map(([path, roles, C, kind]) => (
        <Route key={path} path={path} element={<Guard roles={roles} seg={path.split("/")[1]}>
          <Suspense key={pathname} fallback={<PageSkeleton kind={kind} />}><C /></Suspense></Guard>} />
      ))}
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
}
